import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, ButtonBase, CircularProgress, Dialog, InputBase, Stack, Typography } from '@mui/material';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded';
import { useAuthStore } from '../../store/authStore';
import { useSalonContextStore } from '../../store/salonContextStore';
import { clientApi } from '../../services/clientService';
import { tokens } from '../../theme';
import { NAV_GROUPS, NAV_PINNED } from './navConfig';
import { PersonAvatar } from '../ui/StatusPill';

interface Result {
  key: string;
  section: 'Strony' | 'Klienci';
  title: string;
  subtitle?: string;
  to: string;
  clientSeed?: string;
  first?: string;
  last?: string;
}

const normalize = (s: string) => s.toLocaleLowerCase('pl-PL').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/ł/g, 'l');

/** Wyszukiwarka pod Ctrl+K: strony z menu i klienci aktywnego salonu. */
export const CommandPalette: React.FC<{ open: boolean; onClose: () => void }> = ({ open, onClose }) => {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const activeSalonId = useSalonContextStore((s) => s.activeSalonId);
  const [query, setQuery] = useState('');
  const [clients, setClients] = useState<Result[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [cursor, setCursor] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);

  const canSearchClients = !!activeSalonId && (role === 'SALON_OWNER' || role === 'STAFF' || role === 'MARKETER');

  const pages = useMemo<Result[]>(() => {
    if (!role) return [];
    const all: Result[] = [];
    NAV_PINNED.filter((i) => i.roles.includes(role)).forEach((i) => all.push({ key: i.path, section: 'Strony', title: i.label, to: i.path, subtitle: i.keywords }));
    NAV_GROUPS.forEach((g) => g.items.filter((i) => i.roles.includes(role)).forEach((i) => all.push({ key: i.path, section: 'Strony', title: i.label, to: i.path, subtitle: `${g.label}|${i.keywords || ''}` })));
    const seen = new Set<string>();
    return all.filter((r) => (seen.has(r.to) ? false : (seen.add(r.to), true)));
  }, [role]);

  const pageResults = useMemo(() => {
    const q = normalize(query.trim());
    const list = q
      ? pages.filter((p) => normalize(`${p.title} ${p.subtitle || ''}`).includes(q))
      : pages.slice(0, 8);
    return list.slice(0, 8).map((p) => ({ ...p, subtitle: p.subtitle?.includes('|') ? p.subtitle.split('|')[0] : undefined }));
  }, [pages, query]);

  useEffect(() => {
    if (!open) { setQuery(''); setClients([]); setCursor(0); }
  }, [open]);

  useEffect(() => {
    const q = query.trim();
    if (!open || !canSearchClients || q.length < 2) { setClients([]); setLoadingClients(false); return undefined; }
    setLoadingClients(true);
    const handle = window.setTimeout(async () => {
      try {
        const res = await clientApi.listBySalon(activeSalonId as string, 1, 6, q);
        const rows = ((res as any)?.data || []) as Array<any>;
        setClients(rows.map((c) => ({
          key: `client-${c.id}`,
          section: 'Klienci',
          title: [c.firstName, c.lastName].filter(Boolean).join(' ') || c.email || c.phone || 'Klient',
          subtitle: [c.phone, c.email].filter(Boolean).join(' · '),
          to: `/salon/clients?client=${encodeURIComponent(c.id)}`,
          clientSeed: c.id,
          first: c.firstName,
          last: c.lastName,
        })));
      } catch {
        setClients([]);
      } finally {
        setLoadingClients(false);
      }
    }, 250);
    return () => window.clearTimeout(handle);
  }, [query, open, canSearchClients, activeSalonId]);

  const results = [...pageResults, ...clients];
  useEffect(() => { setCursor(0); }, [query]);

  const go = (r: Result | undefined) => {
    if (!r) return;
    onClose();
    navigate(r.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(results.length - 1, c + 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(0, c - 1)); }
    if (e.key === 'Enter') { e.preventDefault(); go(results[cursor]); }
  };

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${cursor}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [cursor]);

  let index = -1;
  const renderSection = (title: string, items: Result[]) => (
    items.length > 0 && (
      <Box key={title} sx={{ pb: 1 }}>
        <Typography sx={{ px: 2.25, pt: 1.5, pb: 0.5, fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: tokens.muted }}>
          {title}
        </Typography>
        {items.map((r) => {
          index += 1;
          const i = index;
          const selected = i === cursor;
          return (
            <ButtonBase
              key={r.key}
              data-index={i}
              onMouseEnter={() => setCursor(i)}
              onClick={() => go(r)}
              sx={{
                width: 'calc(100% - 12px)', mx: '6px', px: 1.5, py: 1, borderRadius: '10px', justifyContent: 'flex-start', gap: 1.5, textAlign: 'left',
                bgcolor: selected ? tokens.plumSoft : 'transparent',
              }}
            >
              {r.section === 'Klienci' ? (
                <PersonAvatar firstName={r.first} lastName={r.last} fallback={r.title} seed={r.clientSeed} size={30} />
              ) : (
                <Box sx={{ width: 30, height: 30, borderRadius: '8px', bgcolor: selected ? '#fff' : tokens.surfaceStrong, color: tokens.plum, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <NorthEastRoundedIcon sx={{ fontSize: 16 }} />
                </Box>
              )}
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 600, color: tokens.ink }}>{r.title}</Typography>
                {r.subtitle && <Typography noWrap sx={{ fontSize: '0.8125rem', color: tokens.muted }}>{r.subtitle}</Typography>}
              </Box>
              {selected && <Typography sx={{ fontSize: '0.75rem', color: tokens.muted, fontWeight: 600, display: { xs: 'none', sm: 'block' } }}>Enter</Typography>}
            </ButtonBase>
          );
        })}
      </Box>
    )
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      sx={{ '& .MuiDialog-container': { alignItems: { xs: 'flex-end', sm: 'flex-start' } }, '& .MuiDialog-paper': { mt: { sm: '12vh' } } }}
      aria-label="Wyszukiwarka"
    >
      <Stack direction="row" alignItems="center" gap={1.25} sx={{ px: 2.25, py: 1.5, borderBottom: `1px solid ${tokens.line}` }}>
        <SearchRoundedIcon sx={{ color: tokens.muted }} />
        <InputBase
          autoFocus
          fullWidth
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={canSearchClients ? 'Szukaj strony albo klienta (imię, telefon, e-mail)' : 'Szukaj strony'}
          inputProps={{ 'aria-label': 'Szukaj', role: 'combobox', 'aria-expanded': true }}
          sx={{ fontSize: '1rem', '& input': { py: 0.75 } }}
        />
        {loadingClients && <CircularProgress size={16} />}
        <Box component="kbd" sx={{ fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, color: tokens.muted, border: `1px solid ${tokens.line}`, borderRadius: '6px', px: 0.75, py: 0.1, display: { xs: 'none', sm: 'block' } }}>
          Esc
        </Box>
      </Stack>
      <Box ref={listRef} sx={{ maxHeight: { xs: '60vh', sm: 420 }, overflowY: 'auto', py: 0.5 }}>
        {results.length === 0 && !loadingClients ? (
          <Typography sx={{ px: 2.5, py: 4, textAlign: 'center', color: tokens.muted, fontSize: '0.9375rem' }}>
            Nic nie znaleziono dla „{query}”.
          </Typography>
        ) : (
          <>
            {renderSection('Strony', pageResults)}
            {renderSection('Klienci', clients)}
          </>
        )}
      </Box>
    </Dialog>
  );
};

export default CommandPalette;
